import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { FINAL_CTA, SALES_EMAIL } from "../mock";
import { Reveal, DarkCTA } from "../components/Shared";
import { api, mediaUrl } from "../api";
import { Seo } from "../components/Seo";

let cache = null;
let cachedAt = 0;
export const fetchCaseStudies = async () => {
  if (cache && Date.now() - cachedAt < 60000) return cache;
  const { data } = await api.get("/case-studies");
  cache = data;
  cachedAt = Date.now();
  return data;
};

export const Metrics = ({ metrics, dark = false, compact = false }) => (
  <div className={`grid gap-3 ${metrics.length >= 4 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
    {metrics.map((m) => (
      <div key={m.label}>
        <div className={`font-display font-bold tracking-tight whitespace-nowrap ${compact ? "text-xl" : metrics.length >= 4 ? "text-xl md:text-2xl" : "text-2xl md:text-3xl"} ${dark ? "text-white" : "text-[var(--fr-primary-dark)]"}`}>{m.value}</div>
        <div className={`text-[11px] leading-snug mt-1 ${dark ? "text-white/60" : "text-[var(--fr-muted)]"}`}>{m.label}</div>
      </div>
    ))}
  </div>
);

export const CaseCard = ({ cs, delay = 0, large = false }) => (
  <Reveal delay={delay} className="h-full">
    <Link to={`/case-studies/${cs.slug}`} className={`card card-hover p-3 flex h-full group ${large ? "flex-col lg:flex-row" : "flex-col"}`} data-testid="case-card">
      <div className={`img-tile bg-[var(--fr-bg-alt)] ${large ? "img-tile-contain aspect-[16/9] lg:w-[55%] lg:self-center" : "aspect-[16/10]"}`}>
        {cs.cover && <img src={mediaUrl(cs.cover)} alt={cs.company} loading="lazy" />}
        <span className="absolute top-3 left-3 chip bg-white/90 backdrop-blur">{cs.sector}</span>
      </div>
      <div className={`p-4 flex-1 flex flex-col ${large ? "lg:p-8 lg:justify-center" : ""}`}>
        <div className="flex items-center gap-3">
          {cs.logo && <img src={mediaUrl(cs.logo)} alt="" className="h-7 w-auto max-w-[110px] object-contain" />}
          <span className="text-xs text-[var(--fr-muted)] inline-flex items-center gap-1">
            <MapPin size={11} /> {cs.location}
          </span>
        </div>
        <h3 className={`font-display font-bold tracking-tight leading-snug mt-3 ${large ? "text-2xl md:text-3xl" : "text-[17px]"}`}>{cs.title}</h3>
        <p className={`mt-3 text-[var(--fr-ink-2)] leading-relaxed ${large ? "text-base" : "text-sm line-clamp-3"}`}>{cs.summary}</p>
        {cs.metrics?.length > 0 && (
          <div className="mt-5 pt-5 border-t border-[var(--fr-border)]">
            <Metrics metrics={cs.metrics.slice(0, 3)} compact={!large} />
          </div>
        )}
        <span className="mt-auto pt-5 inline-flex items-center gap-1 text-sm font-semibold text-[var(--fr-primary-dark)] group-hover:gap-2 transition-[gap]">
          Read the story <ArrowRight size={15} />
        </span>
      </div>
    </Link>
  </Reveal>
);

const CaseStudies = () => {
  const [all, setAll] = useState(null);
  const [sector, setSector] = useState("All");

  useEffect(() => {
    fetchCaseStudies().then(setAll).catch(() => setAll([]));
  }, []);

  const sectors = useMemo(() => ["All", ...Array.from(new Set((all || []).map((c) => c.sector).filter(Boolean)))], [all]);
  const list = useMemo(() => (all || []).filter((c) => sector === "All" || c.sector === sector), [all, sector]);
  const [featured, ...rest] = list;
  const filtering = sector !== "All";

  return (
    <>
      <Seo title="Customer stories — Real operations, measurable results" description="How pharmacies, marketplaces, restaurants and 3PLs across the GCC run faster, cheaper and more reliable deliveries on Fleetroot." />
      <section className="pt-16 pb-10 md:pt-24 md:pb-12">
        <div className="container-x">
          <span className="eyebrow-pill fade-up">Customer stories</span>
          <h1 className="h-display mt-6 max-w-4xl fade-up delay-1">Real operations. Measurable results.</h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-[var(--fr-ink-2)] leading-relaxed fade-up delay-2">
            How pharmacies, marketplaces and 3PLs across the GCC run faster, cheaper and more reliable deliveries on Fleetroot.
          </p>
          <div className="mt-10 flex flex-wrap gap-2 fade-up delay-3" data-testid="case-sectors">
            {sectors.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSector(s)}
                className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors ${
                  sector === s ? "bg-[var(--fr-ink)] text-white border-[var(--fr-ink)]" : "bg-white text-[var(--fr-ink-2)] border-[var(--fr-border)] hover:border-[var(--fr-ink)]"
                }`}
                data-testid={`case-sector-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          {all === null ? (
            <div className="card p-12 animate-pulse h-[380px]" data-testid="cases-loading" />
          ) : list.length === 0 ? (
            <div className="card p-12 text-center" data-testid="cases-empty">
              <h3 className="font-display font-bold text-xl">No stories in this sector yet</h3>
              <p className="mt-2 text-[var(--fr-ink-2)]">Try another filter.</p>
            </div>
          ) : (
            <>
              {!filtering && featured && (
                <div className="mb-6" data-testid="case-featured">
                  <CaseCard cs={featured} large />
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="cases-grid">
                {(filtering ? list : rest).map((c, i) => (
                  <CaseCard key={c.slug} cs={c} delay={(i % 3) * 70} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <DarkCTA
        eyebrow="Your story next"
        title="Let's write your results."
        text="Tell us what you move and how many vehicles. A product specialist will map your operation to the outcomes above."
        cta={{ label: "Request a demo", to: "/contact" }}
        secondary={FINAL_CTA.cta}
        email={SALES_EMAIL}
      />
    </>
  );
};

export default CaseStudies;
